import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class LocalizeService {
    private cultureLanguages = [
        { id: 'nl', culture: 'nl-BE' },
        { id: 'en', culture: 'en-GB' },
        { id: 'fr', culture: 'fr-BE' },
        { id: 'de', culture: 'de-BE' }
    ];
    private defaultLanguage = 'nl';
    private defaultCulture = 'nl-BE';

    constructor(private location: PlatformLocation, private translate: TranslateService) {
    }

    public getLanguage(): string {
        const urlLanguage = this.getLanguageFromUrl();
        const storedLanguage = this.getLangaugeFromStorage();
        const browserLanguage = this.getLanguageFromBrowser();

        const language = (urlLanguage || storedLanguage || browserLanguage || this.defaultLanguage);

        return language;
    }

    public getCurrentCulture(): string {
        const currentLanguage = this.cultureLanguages.find(f => f.id === this.getLanguage());
        if (currentLanguage) {
            return currentLanguage.culture;
        }

        return this.defaultCulture;
    }

    /**
     * Used in APP_INITIALIZER
     * to prevent FOUC (flash of untranslated content)
     * and to set the document language.
     */
    public initializeTranslate(): Promise<any> {
        return new Promise<void>(resolve => {
            this.translate.onLangChange.pipe(first()).subscribe((event: LangChangeEvent) => {
                try {
                    localStorage.setItem('LANGUAGE', event.lang);
                } catch (e) {
                    /**
                     * setItem in localstorage throws QUOTA_EXCEEDED_ERR
                     * for safari on iOS and OS X in safari private browser.
                     * This comment is here to keep ESLint happy.
                     */
                }
                document.documentElement.lang = event.lang;
                resolve();
            });

            const newLang = this.getLanguage();
            this.translate.addLangs(this.cultureLanguages.map(l => l.id));
            this.translate.setDefaultLang(this.defaultLanguage);
            this.translate.use(newLang);
        });
    }

    private getLanguageFromBrowser(): string {
        return this.returnIfInLanguages(this.translate.getBrowserLang());
    }

    private getLangaugeFromStorage(): string {
        const lang = localStorage.getItem('LANGUAGE');

        return this.returnIfInLanguages(lang);
    }

    private getLanguageFromUrl(url?: string): string {
        const pathSlices = (url || this.location.pathname || '')
            .split('#')[0]
            .split('?')[0]
            .split('/');

        if (pathSlices.length > 1) {
            return this.returnIfInLanguages(pathSlices[1]);
        }

        if (pathSlices.length) {
            return this.returnIfInLanguages(pathSlices[0]);
        }

        return null;
    }

    private returnIfInLanguages(value: string): string {
        if (value && this.cultureLanguages.some(f => f.id === value)) {
            return value;
        }

        return null;
    }

    public getStadGentDateTimeNotation(date: Date): string {
        if (!date || isNaN(date.getTime())) {
            return '-';
        }

        if(date.getTime() === 0){
            return this.translate.instant('einddatumLater');
        }

        if (date.getFullYear() === 2100) {
            return this.translate.instant('ResultaatBepaaldOnbepaaldeDatum.OnbepaaldeDatum');
        }

        const locale = this.getCurrentCulture();

        if (locale === 'nl-BE') {
            const day = date.getDate();
            const month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date);
            const year = date.getFullYear();
            const hours = date.getHours();
            const minutes = date.getMinutes();

            const timePart = minutes === 0
                ? `${hours}`
                : `${hours}.${minutes.toString().padStart(2, '0')}`;

            return `${day} ${month} ${year} om ${timePart} uur`;
        }

        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        };
        return new Intl.DateTimeFormat(locale, options).format(date);
    }
}
